import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Radio, message, DatePicker } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { m303Api, ReportRow } from '../../services/m303Api';
import './M303Form.css';

// ---- Filter state ----
interface FilterState {
  startLoc: string;
  endLoc: string;
  startProd: string;
  endProd: string;
  startBatch: string;
  endBatch: string;
  startDate: string;
  endDate: string;
  startSrcBatch: string;
  endSrcBatch: string;
  includeZero: boolean;
  includeSpec: boolean;
  clothingSize: boolean;
  reportFormat: 1 | 2;
}

const emptyFilter = (): FilterState => ({
  startLoc: '',
  endLoc: '',
  startProd: '',
  endProd: '',
  startBatch: '',
  endBatch: '',
  startDate: '',
  endDate: '',
  startSrcBatch: '',
  endSrcBatch: '',
  includeZero: false,
  includeSpec: false,
  clothingSize: false,
  reportFormat: 1,
});

// ---- Table columns ----
const columns: ColumnsType<ReportRow> = [
  { title: '庫位', dataIndex: 'fm030_01', key: 'fm030_01', width: 70, fixed: 'left' },
  { title: '庫位名稱', dataIndex: 'loc_name', key: 'loc_name', width: 100, ellipsis: true },
  { title: '產品代碼', dataIndex: 'fm030_02', key: 'fm030_02', width: 110 },
  { title: '批號', dataIndex: 'fm030_03', key: 'fm030_03', width: 110 },
  { title: '品名代碼', dataIndex: 'fm030_04', key: 'fm030_04', width: 90 },
  { title: '倉庫', dataIndex: 'fm030_12', key: 'fm030_12', width: 60 },
  {
    title: '入庫日期',
    dataIndex: 'fm030_13',
    key: 'fm030_13',
    width: 100,
    render: (v: string | null) => (v ? v.substring(0, 10) : ''),
  },
  {
    title: '庫存量',
    dataIndex: 'fm030_16',
    key: 'fm030_16',
    width: 90,
    align: 'right',
    render: (v: number) => v.toFixed(2),
  },
  {
    title: '庫存金額',
    dataIndex: 'fm030_17',
    key: 'fm030_17',
    width: 100,
    align: 'right',
    render: (v: number) => v.toFixed(2),
  },
  {
    title: '平均單價',
    dataIndex: 'fm030_18',
    key: 'fm030_18',
    width: 95,
    align: 'right',
    render: (v: number) => v.toFixed(3),
  },
  { title: '顏色代碼', dataIndex: 'fm030_51', key: 'fm030_51', width: 75 },
  { title: '顏色名稱', dataIndex: 'color_name', key: 'color_name', width: 90, ellipsis: true },
  { title: '等級', dataIndex: 'fm030_52', key: 'fm030_52', width: 55 },
  {
    title: '規格1',
    dataIndex: 'fm030_24',
    key: 'fm030_24',
    width: 65,
    align: 'right',
    render: (v: number) => (v ? v.toFixed(1) : ''),
  },
  {
    title: '規格2',
    dataIndex: 'fm030_25',
    key: 'fm030_25',
    width: 65,
    align: 'right',
    render: (v: number) => (v ? v.toFixed(1) : ''),
  },
  {
    title: '規格3',
    dataIndex: 'fm030_26',
    key: 'fm030_26',
    width: 65,
    align: 'right',
    render: (v: number) => (v ? v.toFixed(1) : ''),
  },
  {
    title: '規格4',
    dataIndex: 'fm030_27',
    key: 'fm030_27',
    width: 65,
    align: 'right',
    render: (v: number) => (v ? v.toFixed(1) : ''),
  },
  {
    title: '在途量',
    dataIndex: 'fm030_31',
    key: 'fm030_31',
    width: 80,
    align: 'right',
    render: (v: number) => (v ? v.toFixed(2) : ''),
  },
  {
    title: '保留量',
    dataIndex: 'fm030_32',
    key: 'fm030_32',
    width: 80,
    align: 'right',
    render: (v: number) => (v ? v.toFixed(2) : ''),
  },
  {
    title: '可用量',
    dataIndex: 'fm030_41',
    key: 'fm030_41',
    width: 80,
    align: 'right',
    render: (v: number) => (v ? v.toFixed(2) : ''),
  },
  { title: '來源批號', dataIndex: 'fm030_66', key: 'fm030_66', width: 110, ellipsis: true },
];

// ---- Component ----
const M303Form: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterState>(emptyFilter());
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [queried, setQueried] = useState(false);

  const setField = useCallback(
    <K extends keyof FilterState>(field: K, value: FilterState[K]) => {
      setFilter((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const onQuery = useCallback(async () => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await m303Api.getReport({
        startLoc: filter.startLoc,
        endLoc: filter.endLoc,
        startProd: filter.startProd,
        endProd: filter.endProd,
        startBatch: filter.startBatch,
        endBatch: filter.endBatch,
        startDate: filter.startDate,
        endDate: filter.endDate,
        startSrcBatch: filter.startSrcBatch,
        endSrcBatch: filter.endSrcBatch,
        includeZero: filter.includeZero,
      });
      setRows(res.items || []);
      setQueried(true);
      if (!res.items || res.items.length === 0) {
        message.info('查無符合條件的庫存資料');
      } else {
        message.success(`查詢到 ${res.total} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  const onClear = useCallback(() => {
    setFilter(emptyFilter());
    setRows([]);
    setQueried(false);
  }, []);

  const onExport = useCallback(() => {
    message.info('匯出功能開發中');
  }, []);

  const onCheck2Change = useCallback((checked: boolean) => {
    setFilter((prev) => ({
      ...prev,
      clothingSize: checked,
      ...(checked ? { includeSpec: false, reportFormat: 1 as const } : {}),
    }));
  }, []);

  // ---- Render ----
  return (
    <div className="m303-form">
      {loading && <div className="loading-overlay">查詢中...</div>}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                庫存管理
              </a>
              <span>/</span>
              <span className="current">庫位批號庫存量表</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>庫位批號庫存量表</h1>
            <span className="badge">M303</span>
          </div>
        </div>

        {/* Filter Panel */}
        <div className="filter-panel">
          {/* Row 1: 庫位代號 */}
          <div className="filter-row">
            <span className="filter-label">庫位代號:[從]</span>
            <Input
              value={filter.startLoc}
              onChange={(e) => setField('startLoc', e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="起始庫位"
              style={{ width: 120 }}
              allowClear
            />
            <span className="filter-sep">[至]</span>
            <Input
              value={filter.endLoc}
              onChange={(e) => setField('endLoc', e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="結束庫位"
              style={{ width: 120 }}
              allowClear
            />
          </div>

          {/* Row 2: 產品代號 */}
          <div className="filter-row">
            <span className="filter-label">產品代號:[從]</span>
            <Input
              value={filter.startProd}
              onChange={(e) => setField('startProd', e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="起始產品代號"
              style={{ width: 120 }}
              allowClear
            />
            <span className="filter-sep">[至]</span>
            <Input
              value={filter.endProd}
              onChange={(e) => setField('endProd', e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="結束產品代號"
              style={{ width: 120 }}
              allowClear
            />
          </div>

          {/* Row 3: 批號 */}
          <div className="filter-row">
            <span className="filter-label">批號:[從]</span>
            <Input
              value={filter.startBatch}
              onChange={(e) => setField('startBatch', e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="起始批號"
              style={{ width: 120 }}
              allowClear
            />
            <span className="filter-sep">[至]</span>
            <Input
              value={filter.endBatch}
              onChange={(e) => setField('endBatch', e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="結束批號"
              style={{ width: 120 }}
              allowClear
            />
          </div>

          {/* Row 4: 入庫日期 */}
          <div className="filter-row">
            <span className="filter-label">入庫日期:[從]</span>
            <DatePicker
              value={filter.startDate ? dayjs(filter.startDate) : null}
              onChange={(date) => setField('startDate', date ? date.format('YYYY-MM-DD') : '')}
              placeholder="起始日期"
              style={{ width: 120 }}
            />
            <span className="filter-sep">[至]</span>
            <DatePicker
              value={filter.endDate ? dayjs(filter.endDate) : null}
              onChange={(date) => setField('endDate', date ? date.format('YYYY-MM-DD') : '')}
              placeholder="結束日期"
              style={{ width: 120 }}
            />
          </div>

          {/* Row 5: 來源批號 */}
          <div className="filter-row">
            <span className="filter-label">來源批號:[從]</span>
            <Input
              value={filter.startSrcBatch}
              onChange={(e) => setField('startSrcBatch', e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="起始來源批號"
              style={{ width: 120 }}
              allowClear
            />
            <span className="filter-sep">[至]</span>
            <Input
              value={filter.endSrcBatch}
              onChange={(e) => setField('endSrcBatch', e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="結束來源批號"
              style={{ width: 120 }}
              allowClear
            />
          </div>

          {/* Row 6: Check1 + Check2 */}
          <div className="filter-row">
            <span className="filter-label" />
            <Checkbox
              checked={filter.includeSpec}
              disabled={filter.clothingSize}
              onChange={(e) => setField('includeSpec', e.target.checked)}
            >
              含產品規格
            </Checkbox>
            <Checkbox
              checked={filter.clothingSize}
              onChange={(e) => onCheck2Change(e.target.checked)}
            >
              服裝尺寸
            </Checkbox>
          </div>

          {/* Row 7: 庫存量為零 */}
          <div className="filter-row">
            <span className="filter-label">庫存量為零:</span>
            <Radio.Group
              value={filter.includeZero}
              onChange={(e) => setField('includeZero', e.target.value)}
            >
              <Radio value={true}>含</Radio>
              <Radio value={false}>排除</Radio>
            </Radio.Group>
          </div>

          {/* Row 8: 報表格式 */}
          <div className="filter-row">
            <span className="filter-label">報表格式:</span>
            <Radio.Group
              value={filter.reportFormat}
              disabled={filter.clothingSize}
              onChange={(e) => setField('reportFormat', e.target.value)}
            >
              <Radio value={1}>A4直印</Radio>
              <Radio value={2}>A4橫印</Radio>
            </Radio.Group>
            <span className="filter-note">(含庫存發生原因)</span>
          </div>

          {/* Buttons row */}
          <div className="filter-row filter-actions">
            <span className="filter-label" />
            <Button type="primary" onClick={onQuery} loading={loading}>
              確定範圍
            </Button>
            <Button onClick={onExport}>
              匯出 Excel
            </Button>
            <Button onClick={onClear}>清除</Button>
          </div>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{rows.length}</strong> 筆
              </div>
            </div>
          )}

          {queried ? (
            <Table<ReportRow>
              columns={columns}
              dataSource={rows}
              rowKey={(r) => `${r.fm030_01}-${r.fm030_02}-${r.fm030_03}-${r.fm030_04}`}
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `共 ${t} 筆` }}
              size="small"
              scroll={{ x: 1600, y: 460 }}
              locale={{ emptyText: '查無資料' }}
              loading={loading}
            />
          ) : (
            <div className="empty-state">
              請設定查詢條件並點擊「確定範圍」
            </div>
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <Button
            danger
            size="large"
            onClick={() => navigate('/erp')}
          >
            離開
          </Button>
        </div>
      </div>
    </div>
  );
};

export default M303Form;
